/*
 * Shared comparison between what CARFAX returned for a vehicle and what the record currently holds.
 *
 * ⚠ THE RULE: a difference exists when CARFAX has a meaningful value AND (the record has no
 * meaningful value OR the two values differ). "The record has nothing" is a difference - it is the
 * whole point of the prompt, because that is the case where CARFAX knows something the record does
 * not.
 *
 * ⚠ Why this exists. Four copies of this comparison had drifted apart, and three shared the same
 * defect: they required the RECORD to already hold a value before they would compare at all.
 *   - modelId was guarded on `selected.modelId > 0`, but -1 is the "nothing selected" sentinel, so
 *     the single most important case (no model chosen, CARFAX found one) was the exact case it
 *     refused to evaluate. That is why the reconcile modal never appeared for a bare "2024 Ferrari
 *     296" while CARFAX was returning "296 GTS Convertible".
 *   - style was guarded on `selected.style`, so null vs "Convertible" read as no difference.
 *   - productionYear compared the CARFAX year against carfaxMakeModel.productionYear - CARFAX
 *     against CARFAX. The model lookup already matches on productionYear, so it was equal by
 *     construction and could never fire.
 * The trim comparison alone handled the empty case correctly, which is what shows these were bugs
 * rather than a deliberate design.
 */

import { readMakeModelFields, resolveCarfaxMakeModel, buildCarfaxCustomMakeModelValues, modelNameCarriesTrim, isMeaningfulValue } from '@/utils/carfax_make_model_resolution'

/*
 * null/undefined-safe lowercase+trim, so both sides normalise identically.
 *
 * ⚠ Internal runs of whitespace are squashed too, not only the ends. Catalogue names are typed by
 * hand and carfax composes its own from separate model and series fields, so "812  Superfast" against
 * "812 Superfast" is a realistic way for two identical names to read as different - and since the
 * model name alone now decides whether the operator is interrupted, that would be an interruption
 * over a space.
 */
const norm = (value) => (value === null || value === undefined)
  ? ''
  : String(value).toLowerCase().replace(/\s+/g, ' ').trim()

/*
 * CARFAX sends placeholders for fields it has no answer for; treat those as "no value", not as a
 * value that happens to differ. Applied to BOTH sides so a stored placeholder behaves the same way.
 *
 * ⚠ IMPORTED, not redefined. This file used to carry its own byte-identical copy, so the reject list
 * existed twice and "absent" could come to mean two different things - which is exactly what
 * happened, with the server rejecting 'Not specified' and the client not.
 */
const isMeaningful = isMeaningfulValue

// True when carfax knows something and the record either does not, or disagrees.
const differs = (carfaxValue, recordValue) => {
  if (!isMeaningful(carfaxValue)) return false
  if (!isMeaningful(recordValue)) return true
  return norm(carfaxValue) !== norm(recordValue)
}

/*
 * The same test with the spaces taken out, for MODEL NAMES ONLY.
 *
 * The catalogue stores "S 580" where CARFAX sends "S580" - the same vehicle. `norm` collapses runs of
 * whitespace but keeps single spaces, so the two read as different and the operator is interrupted
 * over a space. Meeting change #8, stock FP7465.
 *
 * ⚠ Deliberately NOT applied to trim, style, year or make. `norm` also backs `isMeaningful`, and
 * de-spacing a style or a trim is meaningless rather than useful - it would only make two genuinely
 * different values collide. The resolution side draws the same line; see `tightKey` there.
 */
const differsIgnoringSpaces = (carfaxValue, recordValue) => {
  if (!isMeaningful(carfaxValue)) return false
  if (!isMeaningful(recordValue)) return true
  return norm(carfaxValue).replace(/\s/g, '') !== norm(recordValue).replace(/\s/g, '')
}

/**
 * @param selectedMakeModel the record's current vehicle selection (may be null, or populated with
 *        the modelId === -1 sentinel meaning nothing chosen yet)
 * @param carfaxMakeModel   the resolved carfax match built by the caller
 * @param carfaxValues      the raw carfax response fields { series, style, productionYear }
 * @param foundMake         the make matched out of allVehicleMakes, or null
 */
export const compareCarfaxMakeModel = (selectedMakeModel, carfaxMakeModel, carfaxValues, foundMake) => {
  const selected = selectedMakeModel || {}
  const carfax = carfaxMakeModel || {}
  const values = carfaxValues || {}

  const result = {
    vehicleMakeDifferent: false,
    productionYearDifferent: false,
    trimDifferent: false,
    styleDifferent: false,
    modelNameDifferent: false
  }

  if (foundMake) {
    result.vehicleMakeDifferent = !selectedMakeModel || foundMake.id !== selected.makeId
  }

  /*
   * ⚠ THE MODEL IS COMPARED BY NAME, AND ONLY BY NAME. There used to be an id comparison in front of
   * this - `selected.modelId !== carfax.modelId` whenever carfax had resolved a catalogue row - with
   * this name comparison kept as the free-text fallback. The id form cannot answer the question it
   * was being asked: a model held as free text carries the -1 sentinel, which equals no id, so the
   * flag came back true however the two names read. On VIN ZFF83CLA4K0248000 that put the reconcile
   * prompt on screen saying "a different model, 812 Superfast than the one currently selected
   * 812 Superfast" - two identical strings, offered to the operator as a difference to resolve.
   *
   * Names are also the only thing worth comparing here: they are what the prompt quotes, what the
   * choice table lists, and what buildCarfaxOverwriteNote names. readMakeModelFields reads the
   * catalogue row's name or the free-text name from either side, so one line covers both paths.
   *
   * ⚠ No `selected.modelId > 0` precondition - that guard was the original bug. Nothing selected is
   * precisely when carfax's answer is worth offering, and `differs` already treats "the record holds
   * nothing" as a difference.
   */
  /*
   * ⚠ CARFAX SPLITS THE NAME, THE CATALOGUE FOLDS IT, and comparing the halves against the whole
   * reported a difference on a record that is exactly right. CARFAX sends model "296" and series
   * "GTS" as separate fields; the catalogue row is named "296 GTS". Where carfax resolved onto that
   * row the join already happened upstream, but where it did not - no catalogue row for that make and
   * year - the bare "296" arrives here and reads as a different model from the "296 GTS" the record
   * correctly holds. Meeting change #8: measured on dev, 10 of the 18 disagreements among 78 records
   * carrying a carfax model were this and nothing else. The catalogue Name carries the sub-model on
   * 701 of its 1,816 active rows, so it is the common shape.
   *
   * Different means different from BOTH readings, so the fold can only ever remove a false
   * difference. A record holding the bare group "296" against carfax "296" + "GTS" still matches the
   * first candidate and is still reported as carrying no sub-model, through trimDifferent below.
   */
  const carfaxModelName = readMakeModelFields(carfax).model
  const recordFields = readMakeModelFields(selected)
  const recordModelName = recordFields.model
  const carfaxModelReadings = [carfaxModelName]
  if (isMeaningful(carfaxModelName) && isMeaningful(values.series)) {
    carfaxModelReadings.push(carfaxModelName + ' ' + values.series)
  }

  /*
   * ⚠ THE SAME COURTESY FOR THE RECORD, and its absence was the defect. CARFAX got two readings and
   * the record got one, so a vehicle on exactly the right catalogue row was compared as "Gallardo"
   * against "Gallardo LP560-4" and reported a difference nobody could resolve. The catalogue splits
   * the vehicle the other way round from CARFAX: family in name, variant in series.
   *
   * ⚠ Guarded exactly as carfax's is above, or a row whose series is the 'N/A' sentinel builds the
   * reading "Gallardo N/A".
   *
   * ⚠ ADDING READINGS CAN ONLY REMOVE A FALSE DIFFERENCE, which is what makes this safe against a
   * catalogue that is inconsistent about what series means. "296 GTS"/"296" stores the GROUP there,
   * "Gallardo"/"LP560-4" the VARIANT. In the group shape the extra readings are nonsense - "296 GTS
   * 296" and "296" - they match nothing, and the bare name still decides, exactly as before.
   */
  const recordModelReadings = [recordModelName]
  if (isMeaningful(recordModelName) && isMeaningful(recordFields.series)) {
    recordModelReadings.push(recordModelName + ' ' + recordFields.series)
    recordModelReadings.push(recordFields.series)
  }

  const readingsAgree = carfaxModelReadings.some(
    (carfaxReading) => recordModelReadings.some((recordReading) => !differsIgnoringSpaces(carfaxReading, recordReading)))

  /*
   * ⚠ The record may simply know MORE than the report - "Stelvio Ti AWD" against a report saying
   * "Stelvio" + "TI". It is not contradicted, and leaving it different hands the operator something
   * they can never resolve.
   *
   * ⚠ AGAINST CARFAX'S FULLEST READING ONLY, never the bare model. Measured on live stock PPA236105:
   * "911 Turbo S Exclusive Series Cabriolet" contains the bare "911", so a looser test would swallow
   * a real Turbo-S-against-Carrera disagreement. The last reading is the most specific thing CARFAX
   * said, because the joined one is pushed after the bare one.
   *
   * ⚠ modelNameCarriesTrim, not indexOf: it matches whole-word runs, so "911 GT" is not carried by
   * "911 GTS", and it normalises through key/squash, which is what lets the live row "360 Modena  F1"
   * with its double space match "360 MODENA".
   */
  const carfaxFullestReading = carfaxModelReadings[carfaxModelReadings.length - 1]
  const recordCarriesTheReport = recordModelReadings.some((reading) => modelNameCarriesTrim(reading, carfaxFullestReading))

  result.modelNameDifferent = !readingsAgree && !recordCarriesTheReport

  // ⚠ Compared against the RECORD's year, not against carfaxMakeModel's - see the header note.
  result.productionYearDifferent = differs(carfax.productionYear || values.productionYear, selected.productionYear)

  /*
   * ⚠ The other half of the same split. Carfax's series is compared against the record's TRIM, so a
   * record that folded the sub-model into its model name - and therefore holds no trim - reported a
   * trim difference for a value it is already carrying.
   *
   * modelNameCarriesTrim, not indexOf: it matches whole-word runs, so "GT" is not swallowed by a name
   * holding "GTS" and a real trim difference survives.
   *
   * ⚠ ONLY WHEN THE RECORD HOLDS NO TRIM. A record reading "296 GTS" with trim "Lusso" genuinely
   * disagrees with a CARFAX series of "GTS", and suppressing that because the NAME happens to contain
   * "GTS" hides a real difference and paints the icon green. The first draft of this did exactly
   * that; the existing colour-rule test caught it.
   */
  /*
   * ⚠ Every record reading, not the name alone - the same asymmetry one level down. A row storing
   * the family in name and the variant in series ("S-Class" / "S 580") does carry CARFAX's series,
   * just not in the column this rule used to look at, so it reported a trim difference for a value
   * it already holds. Meeting change #8.
   *
   * ⚠ STILL ONLY WHEN THE RECORD HOLDS NO TRIM OF ITS OWN. A record reading "296 GTS" with trim
   * "Lusso" genuinely disagrees with a CARFAX series of "GTS", and suppressing that paints the icon
   * green over a real difference - the first draft of the CARFAX-side fold did exactly that.
   */
  const recordCarriesTheSeries = recordModelReadings.some((reading) => modelNameCarriesTrim(reading, values.series))
  /*
   * ⚠ A trim that is PART OF the carried series agrees too. FC2479 is filed "911 Targa 4 GTS" with
   * trim "GTS" against CARFAX "911" + "Targa 4 GTS": the name holds the whole series and "GTS" is in it,
   * yet only a record with no trim was excused, so it reported a difference and offered "Targa 4 GTS"
   * as a trim. "Lusso" against "GTS" is not part of the series and still differs.
   */
  const recordTrimWithinSeries = isMeaningful(selected.trim) && modelNameCarriesTrim(values.series, selected.trim)
  result.trimDifferent = differs(values.series, selected.trim) &&
    !(recordCarriesTheSeries && (!isMeaningful(selected.trim) || recordTrimWithinSeries))
  result.styleDifferent = differs(values.style, selected.style)

  return result
}

/*
 * Everything between a raw CARFAX rating and the five difference flags: find the make, resolve the
 * model (or fall back to free text), and compare.
 *
 * ⚠ Extracted because this orchestration was duplicated THREE times - the inventory mixin,
 * ApprovePpaAction and the roServiceVehicle mixin - and the quick-view needed a fourth. Three copies
 * kept in step by hand is past the threshold; this is that code moved, not redesigned. Each host
 * keeps its own guards at the call site, including roServiceVehicle's deliberate decision to raise
 * the prompt on make, year and model only.
 *
 * ⚠ The five flags are ALWAYS returned reset, which is a deliberate departure from the three copies:
 * they reset them INSIDE their own `if (make)`, so a rating naming no make left the PREVIOUS run's
 * flags standing and could re-raise the prompt against a comparison that never ran. That was latent
 * while the prompt fired at most once; it becomes reachable the moment repeat runs are allowed, which
 * is exactly what this change introduces. Fixed here rather than left for the caller to trip over.
 *
 * @returns { carfaxMakeModel, differences } - both always fresh objects.
 */
/*
 * The trim to offer from CARFAX's series when a trim difference is reconciled: only the words the
 * record's model readings (name, and the series of a split catalogue row) do not already carry.
 *
 * ⚠ Not the raw series. The resolver folds the series into the MODEL ("911" + "Targa 4 GTS" ->
 * "911 Targa 4 GTS"), so offering it again as a trim repeats the model in the Trim field. A series
 * the model already carries in full offers nothing.
 */
export const carfaxSeriesTrimOffer = (series, selectedMakeModel) => {
  if (!isMeaningful(series)) return ''
  const words = String(series).replace(/\s+/g, ' ').trim().split(' ')
  if (!selectedMakeModel) return words.join(' ')

  const record = readMakeModelFields(selectedMakeModel)
  const carried = new Set([record.model, record.series].filter(isMeaningful).join(' ').split(/\s+/).map(norm))
  return words.filter((word) => !carried.has(norm(word))).join(' ')
}

export const buildCarfaxComparison = (allVehicleMakes, carfaxRating, selectedMakeModel) => {
  const rating = carfaxRating || {}
  const carfaxMakeModel = {
    selectedMake: null,
    modelId: 0,
    selectedModel: null,
    productionYear: 0,
    customMakeModel: null,
    customMakeName: '',
    customModelName: ''
  }

  const make = rating.make
  const model = rating.model
  const style = rating.style
  const productionYear = rating.productionYear
  const series = rating.series

  const differences = {
    vehicleMakeDifferent: false,
    productionYearDifferent: false,
    trimDifferent: false,
    styleDifferent: false,
    modelNameDifferent: false
  }

  /*
   * ⚠ Whether the RECORD's model is a catalogue row, which is a different question from any of the
   * five difference flags and is what the match icon colours red on. Same `modelId > 0` test the
   * reconcile table uses for its own red - one definition of "in the catalogue", so the icon and the
   * table cannot disagree about the same record.
   *
   * ⚠ No model chosen at all counts too. It is not literally "free text entered", but it is equally
   * not a catalogue link, and applying a row from the prompt is the same fix for both.
   */
  const recordModelIsFreeText = !(selectedMakeModel && selectedMakeModel.modelId > 0)

  if (!make) return { carfaxMakeModel: carfaxMakeModel, differences: differences, recordModelIsFreeText: recordModelIsFreeText }

  const makes = allVehicleMakes || []
  const foundMake = makes.find((x) => x.name.toUpperCase() === make.toUpperCase())
  if (!foundMake) return { carfaxMakeModel: carfaxMakeModel, differences: differences, recordModelIsFreeText: recordModelIsFreeText }

  carfaxMakeModel.selectedMake = foundMake

  //See utils/carfax_make_model_resolution, including why it does NOT try to match on Model.series:
  //those passes were built, replayed against all 399 stored ratings, and gained nothing - the cases
  //that fail have no catalogue row at all.
  const resolved = resolveCarfaxMakeModel(foundMake, { model, series, style, productionYear })
  if (resolved.resolved) {
    carfaxMakeModel.modelId = resolved.modelId
    carfaxMakeModel.selectedModel = resolved.selectedModel
    carfaxMakeModel.productionYear = resolved.productionYear
    carfaxMakeModel.style = resolved.style
    carfaxMakeModel.trim = resolved.trim
  } else {
    Object.assign(carfaxMakeModel, buildCarfaxCustomMakeModelValues(make, productionYear, model, series, style))
  }

  /*
   * ⚠ What CARFAX ACTUALLY SENT, kept beside whatever it resolved to.
   *
   * The resolver joins the two - key(`${model} ${series}`) - so once a row matches, the only model
   * name left on this object is the joined one ("911 Turbo S"). The reconcile modal then searched for
   * siblings using that, which can only ever find rows BELOW it, never the rest of the group: a 2021
   * Porsche resolved to "911 Turbo S" could not offer "911 Turbo", which exists for the same year.
   *
   * Set on BOTH branches so the modal behaves the same whether or not the joined name matched.
   * Additive - nothing else reads these.
   */
  carfaxMakeModel.sourceModelName = model
  carfaxMakeModel.sourceSeries = series

  const compared = compareCarfaxMakeModel(selectedMakeModel, carfaxMakeModel, { series, style, productionYear }, foundMake)
  differences.vehicleMakeDifferent = compared.vehicleMakeDifferent
  differences.modelNameDifferent = compared.modelNameDifferent
  differences.productionYearDifferent = compared.productionYearDifferent
  differences.trimDifferent = compared.trimDifferent
  differences.styleDifferent = compared.styleDifferent

  return { carfaxMakeModel: carfaxMakeModel, differences: differences, recordModelIsFreeText: recordModelIsFreeText }
}

/*
 * What a comparison actually says, in the three states it can be in - which is the part a bare "are
 * any flags set" test gets wrong.
 *
 * ⚠ All five flags false means "nothing differs" ONLY when the comparison ran. buildCarfaxComparison
 * also returns them all false when the rating names no make, and when it names a make the catalogue
 * does not hold - 99 active makes hold no catalogue models at all - and calling that a match would
 * tell the operator the record is fine when nothing was checked. selectedMake is the discriminator:
 * it is the one field set before any comparing happens and left null in both of those cases.
 */
/*
 * Did the comparison actually RUN? Shared by the outcome and by the prompt gate, which had the same
 * condition written out twice.
 *
 * ⚠ THE MODEL IS PART OF THE TEST, and leaving it out was a real defect. Every difference flag comes
 * back false both when everything agreed and when there was nothing to compare against, so a report
 * naming a make but supplying a placeholder model - the string 'null', on 6 live records - returned
 * "matches" and told the operator in green that the vehicle agreed with a report that had said
 * nothing about it. No colour is the existing answer for a comparison that could not run.
 */
const comparisonCouldRun = (comparison) => {
  if (!comparison || !comparison.carfaxMakeModel || !comparison.carfaxMakeModel.selectedMake) return false
  return isMeaningful(readMakeModelFields(comparison.carfaxMakeModel).model)
}

export const carfaxMatchOutcome = (comparison) => {
  if (!comparisonCouldRun(comparison)) return 'unknown'

  const differences = comparison.differences || {}
  const differs = differences.vehicleMakeDifferent === true ||
    differences.productionYearDifferent === true ||
    differences.modelNameDifferent === true ||
    differences.trimDifferent === true ||
    differences.styleDifferent === true

  return differs ? 'differs' : 'matches'
}

/*
 * The ONE reason the reconcile prompt interrupts: carfax names a different model than the record
 * holds.
 *
 * ⚠ DELIBERATELY NARROWER THAN carfaxMatchOutcome ABOVE, which still answers for all five fields.
 * The two are different jobs. The match icon is a passive indicator: it colours on the comparison so
 * a difference in trim, style or year stays discoverable without interrupting anyone. The prompt
 * INTERRUPTS, and it exists to ask one answerable question: which model should this record hold.
 * Raising it over a trim of "-" against "Superfast" produced a modal quoting two identical model
 * names at the operator with a choice table containing one row - see VIN ZFF83CLA4K0248000. A
 * coloured icon with no prompt is the intended split.
 *
 * ⚠ This block used to state the colour rule itself, and went stale when the rule moved - twenty-five
 * lines above the correct version, contradicting it. carfaxMatchIconClass below is the one place the
 * rule is written down.
 *
 * ⚠ Extracted because the four hosts each hand-rolled this condition and had already drifted: the
 * inventory mixin and ApprovePpaAction tested five flags, the two RO screens tested three.
 *
 * ⚠ The selectedMake guard is the same discriminator carfaxMatchOutcome uses. The flags come back all
 * false both when the comparison agreed and when it could not run at all - no make on the rating, or
 * a make the catalogue does not hold - and neither of those is a reason to interrupt anyone.
 */
export const carfaxModelNameDiffers = (comparison) => {
  if (!comparisonCouldRun(comparison)) return false
  return (comparison.differences || {}).modelNameDifferent === true
}

/*
 * How the CARFAX match indicator looks, and what its tooltip promises.
 *
 * ⚠ EXTRACTED so two surfaces cannot drift. The selector's footer icon and EditInventory's icon
 * below the CARFAX control answer the same question about the same record; this file already exists
 * because four hosts hand-rolled carfaxModelNameDiffers and had already disagreed about it.
 *
 * ⚠ THREE COLOURS, checked in this order:
 *   RED   - the record's model is free text. Checked FIRST, so this wins even when all five fields
 *           agree: a record can match CARFAX perfectly and still carry no catalogue link, which is
 *           the case nothing else on the screen flags. Dean, 22 Sep.
 *   GREEN - a catalogue model and all five fields agree.
 *   BLUE  - a catalogue model, but something differs. Worth seeing, not worth an interruption.
 *
 * ⚠ RED NO LONGER MEANS "the model differs". It used to, keyed on carfaxModelNameDiffers - which is
 * still what raises the PROMPT and is deliberately untouched. Three consequences worth expecting:
 * a catalogue-backed record whose model disagrees with CARFAX is now BLUE rather than red; a
 * free-text record whose model agrees while trim or style does not is now RED rather than blue; and
 * a free-text record that agrees on everything is now RED rather than green. That last one is the
 * largest population - roughly 20,000 records carry a free-text model and a rating.
 *
 * ⚠ NO COLOUR for 'unknown'. Red would claim a mismatch nobody found and green a match nobody
 * checked; the comparison cannot run without a make the catalogue holds. Checked before the free-text
 * test, so "we could not compare" is never reported as a fault in the record.
 */
export const carfaxMatchIconClass = (comparison) => {
  const outcome = carfaxMatchOutcome(comparison)
  if (outcome === 'unknown') return ''
  if (comparison && comparison.recordModelIsFreeText) return 'has-text-danger'
  return outcome === 'matches' ? 'has-text-success' : 'has-text-link'
}

/*
 * ⚠ It TRACKS THE COLOUR above, or the tooltip promises something the click does not do - blue must
 * not say "the vehicle differs" when the model it would offer to fix already agrees. The icon carries
 * no label, so the hover text is the whole affordance.
 *
 * ⚠ Every variant ends by saying it costs nothing. The control compares against the report already
 * on the record; an operator who reads it as "fetch a new CARFAX" will avoid it on the grounds of
 * speed, which is the complaint that produced this work in the first place.
 *
 * `capability` is what THIS reader can do about a difference from where they are:
 *   'pick'      - the screen raises the model picker and the reader may use it (the default).
 *   'elsewhere' - the reader may correct the model, but not here: a grid row, whose click opens the
 *                 quick view with the comparison raised.
 *   'none'      - the reader may not modify inventory, so nothing is offered at all.
 * A boolean is still accepted - true is 'pick', false is 'elsewhere' - for the callers written before
 * the third state existed.
 */
const HOVER_CAPABILITIES = ['pick', 'elsewhere', 'none']

export const carfaxMatchHoverText = (comparison, capability = 'pick') => {
  if (capability === true) capability = 'pick'
  if (capability === false) capability = 'elsewhere'
  if (HOVER_CAPABILITIES.indexOf(capability) === -1) capability = 'none'
  const canPickModel = capability === 'pick'
  const outcome = carfaxMatchOutcome(comparison)
  const noNewReport = ' This does not request a new report.'

  //⚠ Before the free-text test, so a comparison that could not run is never described as a fault in
  //the record. The icon carries no colour here either.
  if (outcome === 'unknown') {
    return 'Check this vehicle against the CARFAX report already stored on the record.' + noNewReport
  }

  /*
   * ⚠ AN OFFER NEEDS BOTH ANSWERS, and getting only part of it right is what this cost three times.
   *
   *   WOULD a picker raise?  carfaxModelNameDiffers above - the predicate every host that HAS a
   *                          picker gates it on (mixins/inventory/index.js:1149,
   *                          ApprovePpaAction.vue:1790, mixins/roServiceVehicle/index.js:165,
   *                          MakeModelSelector's inlineCarfaxCanApply).
   *   CAN this reader, here? capability, which the util cannot derive - it is a pure function of
   *                          the comparison and has no idea which screen rendered it or who is
   *                          reading it.
   *
   * ⚠ The grids are the screens that cannot pick: their click opens the quick view. The quick view
   * CAN since meeting change #10 - its Edit raises the isolated model selector - but only for a
   * reader holding inventory.modify, so it passes 'pick' or 'none' by that permission.
   *
   * ⚠ ABOVE the free-text test, and read by BOTH colours, because it is a fact about the click and
   * not about the record's model. It lived inside the red branch and blue could not see it - which
   * is how blue went on promising "Click to see what changed" while its click raised the model
   * picker, a prompt that deliberately carries no field-by-field table at all.
   *
   * ⚠ Three times, because: the colour first moved to recordModelIsFreeText while the click paths
   * stayed on carfaxMatchOutcome, so "Click to pick a model" appeared where the click answers
   * "already matches"; then the fix for that read carfaxModelNameDiffers as the whole question, when
   * it is half; then the fix for THAT was scoped to red alone. One definition, both colours, now.
   *
   * ⚠ A flag that picks the CLOSING CLAUSE only - never the sentence. carfax_make_model_resolution
   * keeps five separate builders rather than one with a flag, because overloading a builder once
   * turned its text into "a different model, not set than the one currently selected not set".
   * That rule is about forking a message body; the body here is identical either way.
   */
  const offerModelChoice = canPickModel && carfaxModelNameDiffers(comparison)

  if (comparison && comparison.recordModelIsFreeText) {
    /*
     * ⚠ THE CASE THE RED RULE EXISTS FOR, and it needs saying out loud precisely because nothing
     * else on the screen does. Every value agrees with CARFAX, so "differs" would be a lie and green
     * would hide the real problem: the model is free text, so the record carries no catalogue link
     * and drops out of model reporting.
     *
     * ⚠ It promises NOTHING. 'matches' means all five fields agree, so the model name agrees too and
     * no picker can open - the click raises an "already matches" toast. There is also usually nothing
     * to pick: the candidate search returns none once a row is named exactly the CARFAX model name.
     */
    if (outcome === 'matches') {
      return 'This vehicle matches the CARFAX report, but its model is not in the vehicle make/model ' +
        'data - it is held as free text, so this record has no catalogue link.' + noNewReport
    }

    /*
     * ⚠ THREE REASONS NOT TO OFFER, and they deserve different words. Where the model AGREES no screen
     * would raise a picker, so there is nothing to point at. Where the SCREEN cannot pick, the fix
     * exists one click away - the grid icon opens the quick view, where it is corrected. Where the
     * READER may not modify inventory, there is no fix to point them at at all.
     *
     * ⚠ This once said "the model is changed on the edit screen". Since meeting change #10 it is not:
     * the quick view corrects it in place, and the edit screen is the route #10 removed.
     */
    const seeWhatDiffers = capability === 'elsewhere'
      ? ' Click to see what differs and correct it from the vehicle\'s quick view.'
      : ' Click to see what differs.'

    //⚠ Neither side has a catalogue model, so applying CARFAX's would write free text too. Say so,
    //rather than promise a fix the click cannot perform.
    if (!(comparison.carfaxMakeModel && comparison.carfaxMakeModel.modelId > 0)) {
      return 'This vehicle differs from the CARFAX report, and neither its model nor the one on the ' +
        'report is in the vehicle make/model data.' +
        (offerModelChoice ? ' Click to review and choose a model.' : seeWhatDiffers) + noNewReport
    }

    return 'This vehicle differs from the CARFAX report, and its model is not in the vehicle ' +
      'make/model data - it is held as free text.' +
      (offerModelChoice ? ' Click to review and pick the model CARFAX found.' : seeWhatDiffers) + noNewReport
  }

  if (outcome === 'matches') return 'This vehicle matches the CARFAX report. Click to check again.' + noNewReport

  /*
   * ⚠ Blue can no longer claim "the model matches" - it now also covers a catalogue-backed record
   * whose model DOES disagree, which used to be red. That sentence would contradict the table the
   * click opens.
   *
   * ⚠ AND IT CANNOT PROMISE A DIFF WHEN THE MODEL DIFFERS. requestCarfaxMakeModelCheck raises the
   * model prompt and RETURNS EARLY (mixins/inventory/index.js:1358), so the field-difference branch
   * below it never runs - and the prompt it raised carries no field-by-field table by design. The
   * operator was told "see what changed" and handed a model choice with no diff in it.
   *
   * ⚠ ONE offer here, not the two red uses. Red splits on whether CARFAX resolved a catalogue row
   * because that changes what can be applied; blue's defining fact is that the RECORD already holds
   * one, and the picker asks the same single question either way.
   *
   * ⚠ No edit-screen pointer either, unlike red. A blue record has a proper catalogue model - there
   * is nothing to repair, only a difference to review - so naming the edit screen would invent an
   * errand. Where the model agrees, or the host cannot pick, "see what changed" is exactly right:
   * that click opens the five-field comparison.
   */
  return 'This vehicle differs from the CARFAX report.' +
    (offerModelChoice ? ' Click to review and choose a model.' : ' Click to see what changed.') +
    noNewReport
}
